import { z } from 'zod';
import type { ToolReadingDto } from './gaps';
import { TRIADE_PARTS, type Triade, type TriadePart, type Visao } from './strategy';

/* ─────────────────────────────────────────────────────────────────────
   Tríade de Valor e Visão — a fórmula da frase-síntese, os insumos da
   visão e as leituras do agente: a Dica da IA (que só aparece quando
   há algo a melhorar) e a visão finalizada, com palavras-chave e o
   alinhamento ao propósito.
   ───────────────────────────────────────────────────────────────────── */

const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
/** conectores que a pessoa às vezes repete dentro do campo */
const LEADS: Record<TriadePart, RegExp> = {
  believe: /^(acreditamos|nos acreditamos)\s+(em|no|na|nos|nas|que)\s+/,
  through: /^(por meio|atraves)\s+(de|do|da|dos|das)\s+/,
  result:  /^(resultando|que resulta)\s+(em|no|na|nos|nas)\s+/,
};

/** Uma parte pronta para entrar no meio da frase: sem o conector repetido, sem ponto final, com minúscula. */
export function triadePiece(part: TriadePart, text: string): string {
  let t = text.trim().replace(/\s+/g, ' ').replace(/[.;!\s]+$/, '');
  const m = LEADS[part].exec(fold(t));
  if (m) t = t.slice(m[0].length);
  /* minúscula, a não ser que seja sigla ("ESG", "IA") */
  if (/^[A-ZÀ-Ý][a-zà-ÿ]/.test(t)) t = t.charAt(0).toLowerCase() + t.slice(1);
  return t;
}

export type TriadeSegment = { text: string; part: TriadePart | null };

/** A frase pela fórmula, em pedaços: conectores (part = null) e as partes da organização. */
export function triadeSegments(t: Pick<Triade, TriadePart>): TriadeSegment[] {
  const out: TriadeSegment[] = [];
  for (const p of TRIADE_PARTS) {
    const piece = triadePiece(p.key, t[p.key] ?? '');
    if (!piece) continue;
    const lead = out.length ? `, ${p.lead.toLowerCase()} ` : `${p.lead} `;
    out.push({ text: lead, part: null }, { text: piece, part: p.key });
  }
  if (out.length) out.push({ text: '.', part: null });
  return out;
}

export const composeTriade = (t: Pick<Triade, TriadePart>) => triadeSegments(t).map(s => s.text).join('');

/** A frase acompanha as partes enquanto ninguém a reescreveu. */
export function withTriadeSynthesis(t: Triade): Triade {
  if (t.synthesisEdited && t.synthesis.trim()) return t;
  return { ...t, synthesisEdited: false, synthesis: composeTriade(t).slice(0, 500) };
}

/** Projetos antigos: a visão escrita à mão, sem insumos, é da organização — a IA não a substitui. */
export function withVisaoDefaults(v: Visao): Visao {
  if (v.visionEdited !== undefined) return v;
  const inputs = [v.arrive, v.become, v.recognized, v.impact].some(x => x.trim());
  return { ...v, visionEdited: !!v.vision.trim() && !inputs };
}

/** Insumos suficientes para a IA escrever a visão: dois dos quatro campos. */
export const visaoReady = (v: Visao) => [v.arrive, v.become, v.recognized, v.impact].filter(x => x.trim().length >= 8).length >= 2;
/** A tríade está completa o bastante para a IA opinar. */
export const triadeReady = (t: Triade) => [t.believe, t.through, t.result].every(x => x.trim().length >= 6);

/* ── as leituras do agente ────────────────────────────────────────── */

export const TRIADE_TIP_TARGETS = ['believe', 'through', 'result', 'synthesis'] as const;
export type TriadeTipTarget = (typeof TRIADE_TIP_TARGETS)[number];

export const triadeTipSchema = z.object({
  /** a tríade está boa: nada a sugerir */
  ok: z.boolean(),
  /** o que melhorar e por quê, em uma frase */
  dica: z.string().trim().max(280).default(''),
  /** onde aplicar a sugestão */
  campo: z.enum(TRIADE_TIP_TARGETS).nullable().default(null),
  /** o texto reescrito daquele campo, pronto para usar (pode vir vazio) */
  sugestao: z.string().trim().max(500).default(''),
}).refine(t => t.ok || t.dica.length >= 10, { message: 'dica vazia' });
export type TriadeTip = z.infer<typeof triadeTipSchema>;
export type TriadeTipDto = ToolReadingDto<TriadeTip>;

export const visaoReadingSchema = z.object({
  /** a visão finalizada (ou, se a organização reescreveu, uma alternativa) */
  vision: z.string().trim().min(20).max(600),
  keywords: z.array(z.string().trim().min(2).max(40)).min(1).max(6),
  /** o quanto a visão está alinhada ao propósito, 0–100 */
  alignment: z.number().int().min(0).max(100),
  motivo: z.string().trim().max(240).default(''),
});
export type VisaoReading = z.infer<typeof visaoReadingSchema>;
export type VisaoReadingDto = ToolReadingDto<VisaoReading>;
